import { fontVariants } from "theme/fontVariants";
import {
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  space
} from "theme/themeConfig";
import { colours } from "@shared/ui.design-system";

const borderWidth = "0.15rem";

export const Button = {
  baseStyle: {
    ...fontVariants.book1,
    backgroundColor: colours.primary.dark,
    color: colours.secondary.light,
    borderRadius: "2px",
    borderWidth,
    borderColor: colours.primary.dark,
    fontFamily: fonts.demi,
    minHeight: space["6.5"],
    mt: 1,
    fontWeight: fontWeights.medium,
    textTransform: "capitalize",
    "&:hover": {
      textDecoration: "underline"
    },
    "&:hover,&:focus": {
      backgroundColor: colours.primary.dark
    },
    "&:hover&:disabled": {
      color: `${colours.secondary.light} !important`,
      backgroundColor: colours.primary.dark,
      textDecoration: "none"
    }
  },
  variants: {
    ...fontVariants,
    secondary: {
      backgroundColor: colours.secondary.light,
      color: colours.primary.dark,
      "&:hover,&:focus": {
        backgroundColor: colours.secondary.light
      }
    },
    atbRoutineDesktop: {
      background: colours.secondary.light,
      width: "100%",
      fontFamily: fonts.acuminMedium,
      fontSize: "12px",
      lineHeight: "18px",
      fontWeight: 500,
      position: "absolute",
      top: "calc(90% - 30px)",
      left: 0,
      right: 1,
      zIndex: "2",
      minHeight: "2.625rem",
      color: colours.primary.dark,
      border: `2px solid ${colours.primary.dark}`,
      textTransform: "none",
      pointerEvents: "auto",
      "&:hover,&:focus,&:active": {
        color: colours.secondary.light,
        backgroundColor: colours.primary.dark,
        textDecoration: "none"
      }
    },
    atbRoutineMobile: {
      background: colours.secondary.light,
      width: "100%",
      fontFamily: fonts.acuminMedium,
      fontSize: "12px",
      lineHeight: "18px",
      minHeight: "2.5rem",
      fontWeight: 500,
      color: colours.primary.dark,
      border: `2px solid ${colours.primary.dark}`,
      textTransform: "none",
      pointerEvents: "auto",
      "&:hover,&:focus,&:active": {
        color: colours.secondary.light,
        backgroundColor: colours.primary.dark,
        textDecoration: "none"
      }
    },
    atbDesktop: {
      background: `${colours.info.dark}`,
      width: "calc(100% - 32px)",
      fontFamily: fonts.acuminMedium,
      fontSize: "12px",
      lineHeight: "18px",
      fontWeight: 400,
      position: "absolute",
      top: "calc(50% - 32px)",
      left: 1,
      right: 1,
      zIndex: "4",
      border: "none",
      color: colours.secondary.light,
      textTransform: "none",
      pointerEvents: "auto",
      "&:hover,&:focus,&:active": {
        backgroundColor: colours.transparency.dark
      }
    },
    atbMobile: {
      marginTop: 0,
      fontFamily: fonts.acuminMedium,
      fontSize: "12px",
      lineHeight: "18px",
      fontWeight: 400,
      border: "none",
      textTransform: "none",
      borderRadius: "2px",
      background: `${colours.info.dark} `,
      color: colours.secondary.light,
      transition: "all 0.4s ease",
      "&:focus,&:active": {
        background: colours.transparency.dark
      },
      "&:disabled": {
        background: `${colours.primary.dark} !important`,
        opacity: "0.5 !important",
        textDecoration: "none"
      }
    },
    member: {
      backgroundColor: colours.primary.dark,
      borderRadius: "2px",
      color: colours.secondary.light,
      "&:hover,&:focus": {
        backgroundColor: colours.primary.dark
      }
    },
    countrySelector: {
      border: "0",
      backgroundColor: colours.secondary.light,
      color: colours.primary.dark,
      marginTop: "0",
      height: space["2"],
      "&:hover,&:focus": {
        backgroundColor: "transparent"
      }
    },
    transparent: {
      padding: 0,
      marginTop: 0,
      backgroundColor: "transparent",
      border: 0,
      borderRadius: "2px",
      color: colours.primary.dark,
      textTransform: "none",
      minHeight: "initial",
      "&:hover,&:focus": {
        backgroundColor: "transparent"
      }
    },
    transparentNoUnderline: {
      padding: 0,
      marginTop: 0,
      borderRadius: "2px",
      backgroundColor: "transparent",
      border: 0,
      color: colours.primary.dark,
      textTransform: "none",
      "&:hover": {
        textDecoration: "none"
      },
      "&:hover,&:focus": {
        backgroundColor: "transparent"
      }
    },
    link: {
      ...fontVariants.acuminRegular,
      border: "0",
      backgroundColor: colours.secondary.light,
      color: colours.primary.dark,
      borderRadius: "2px",
      textDecoration: "underline",
      "&:hover,&:focus": {
        backgroundColor: "transparent"
      }
    },
    menuLink: {
      ...fontVariants.book1,
      border: "0",
      padding: 0,
      margin: 0,
      borderRadius: "2px",
      height: space["1"],
      backgroundColor: colours.secondary.light,
      color: colours.primary.dark,
      "&:hover,&:focus": {
        backgroundColor: "transparent",
        textDecoration: "none"
      }
    },
    pdpLink: {
      ...fontVariants.acuminLight,
      textDecoration: "underline",
      textTransform: "none",
      backgroundColor: "transparent",
      marginLeft: "-2px",
      fontSize: 1,
      lineHeight: 1.5,
      border: "none",
      marginRight: "-2px",
      color: colours.primary.dark,
      "&:active, &:focus": {
        textDecoration: "underline",
        backgroundColor: "transparent"
      },
      "&:hover": {
        backgroundColor: "transparent !important"
      }
    },
    modalClose: {
      ...fontVariants.caps1,
      backgroundColor: colours.secondary.light,
      color: colours.primary.dark,
      border: "none",
      borderRadius: "2px",
      "&:hover,&:focus": {
        backgroundColor: "transparent"
      }
    },
    pink: {
      backgroundColor: colours.accent.soft,
      borderRadius: "2px",
      borderColor: colours.accent.soft,
      color: colours.primary.dark,
      "&:hover,&:focus": {
        backgroundColor: colours.accent.medium,
        borderColor: colours.accent.medium
      }
    },
    heroBannerCta: {
      ...fontVariants.caps1,
      backgroundColor: colours.accent.medium,
      color: colours.primary.dark,
      border: "none",
      borderRadius: "2px",
      "&:hover,&:focus": {
        backgroundColor: colours.accent.medium
      }
    },
    stickyFooterBtn: {
      backgroundColor: colours.accent.medium,
      outline: "none",
      border: "none",
      color: colours.primary.dark,
      textTransform: "none",
      "&:hover,&:focus": {
        backgroundColor: colours.accent.medium
      }
    },
    annualMembershipCta: {
      ...fontVariants.acuminMedium,
      backgroundColor: "none",
      outline: "none",
      border: "1px solid black",
      color: colours.primary.dark,
      minHeight: 2.5,
      textTransform: "none",
      "&:hover,&:focus": {
        backgroundColor: "none"
      }
    },
    breadcrumb: {
      ...fontVariants.acuminLight,
      marginRight: "30px",
      marginTop: "0px",
      paddingLeft: 0,
      paddingRight: 0,
      position: "relative",
      background: "transparent",
      color: colours.primary.dark,
      fontWeight: "400",
      display: "inline-block",
      textTransform: "capitalize",
      border: "none",
      fontSize: "12px",
      verticalAlign: "top",
      "&::before": {
        position: "absolute",
        content: "'/'",
        right: "-20px",
        pointerEvents: "none",
        top: "auto",
        color: colours.primary.dark,
        opacity: "0.2"
      },
      "&:last-child": {
        marginRight: "0"
      },
      "&.active": {
        position: "inherit",
        color: colours.primary.dark
      },
      "&:focus": {
        boxShadow: "0",
        background: `${colours.secondary.soft}`
      }
    },
    guestMembershipCta: {
      ...fontVariants.acuminMedium,
      backgroundColor: colours.primary.dark,
      outline: "none",
      border: "none",
      color: colours.primary.dark,
      textTransform: "none",
      "&:hover,&:focus": {
        backgroundColor: colours.primary.dark
      }
    },
    guestMembershipGreyCta: {
      ...fontVariants.acuminMedium,
      backgroundColor: colours.primary.dark,
      outline: "none",
      border: "none",
      color: colours.primary.dark,
      textTransform: "none",
      "&:hover,&:focus": {
        backgroundColor: colours.primary.dark
      }
    },
    latestAndGreatestCta: {
      ...fontVariants.acuminRegular,
      backgroundColor: colours.accent.medium,
      outline: "none",
      border: "none",
      textTransform: "none",
      "&:hover,&:focus": {
        backgroundColor: colours.accent.medium
      },
      color: colours.primary.dark
    },
    educationalPopupCta: {
      ...fontVariants.acuminMedium,
      backgroundColor: colours.secondary.light,
      outline: "none",
      border: "1px solid black",
      color: colours.primary.dark,
      textTransform: "none",
      textDecoration: "none",
      "&:hover,&:focus,&active": {
        backgroundColor: colours.secondary.soft,
        borderColor: colours.secondary.deep,
        textDecoration: "none"
      }
    },
    educationalPopupPinkCta: {
      ...fontVariants.acuminMedium,
      backgroundColor: colours.accent.soft,
      outline: "none",
      border: `1px solid ${colours.accent.soft}`,
      color: colours.primary.dark,
      textTransform: "none",
      textDecoration: "none",
      "&:hover,&:focus,&active": {
        backgroundColor: colours.accent.medium,
        borderColor: colours.accent.medium,
        textDecoration: "none"
      }
    },
    plpFilters: {
      backgroundColor: colours.primary.dark,
      color: colours.secondary.light,
      fontFamily: fonts.acuminMedium,
      padding: "8px 10px",
      minHeight: "2.5rem",
      mr: -1,
      mb: -1,
      mt: 0,
      "&:hover,&:focus,&active": {
        textDecoration: "none"
      }
    },
    plpFiltersClearAll: {
      backgroundColor: colours.secondary.light,
      borderColor: colours.secondary.light,
      color: colours.primary.dark,
      fontFamily: fonts.acuminMedium,
      mb: -1,
      mt: 0,
      minH: "2.5rem",
      "&:hover,&:focus,&active": {
        backgroundColor: colours.secondary.light,
        borderColor: colours.secondary.light
      }
    },
    plpFiltersClear: {
      backgroundColor: colours.secondary.light,
      borderColor: colours.secondary.light,
      color: colours.primary.dark,
      fontFamily: fonts.acuminMedium,
      minH: "2.5rem",
      fontSize: -2,
      position: "absolute",
      right: "0",
      top: "-1.5rem",
      p: 0,
      "&:hover,&:focus,&active": {
        backgroundColor: colours.secondary.light,
        borderColor: colours.secondary.light
      }
    },
    tertiary: {
      px: 1,
      py: "-1",
      marginTop: 0,
      fontFamily: fonts.acuminMedium,
      backgroundColor: "transparent",
      borderRadius: "2px",
      color: colours.primary.dark,
      textTransform: "none",
      minHeight: "initial",
      border: `1px solid ${colours.primary.dark}`,
      "&:hover,&:focus": {
        backgroundColor: "transparent"
      }
    }
  },
  sizes: {
    micro: {
      fontSize: fontSizes["-1"],
      padding: `calc(${lineHeights["-4"]} - ${borderWidth}) calc(${lineHeights["-1"]} - ${borderWidth})`
    },
    small: {
      fontSize: fontSizes["0"],
      padding: `calc(${lineHeights["-4"]} - ${borderWidth}) calc(${lineHeights["0"]} - ${borderWidth})`
    },
    medium: {
      fontSize: fontSizes["0"],
      padding: `calc(${lineHeights["-4"]} - ${borderWidth}) calc(${lineHeights["0"]} - ${borderWidth})`
    },
    large: {
      fontSize: fontSizes["2"],
      padding: `calc(${lineHeights["-2"]} - ${borderWidth}) calc(${lineHeights["1"]} - ${borderWidth})`
    },
    huge: {
      fontSize: fontSizes["3"],
      padding: `calc(${lineHeights["-2"]} - ${borderWidth}) calc(${lineHeights["1"]} - ${borderWidth})`
    }
  }
};
